import type { TokenTools } from '@ardrive/turbo-sdk';
import { TurboAuthenticatedClient, TurboFactory } from '@ardrive/turbo-sdk/web';
import { buildTurboGatewayUrl } from '../../../../shared/arweave/publicArweaveGateway';
import {
  ARDRIVE_WALLET_COMPATIBILITY_ERROR,
  isUserRejectedError,
  isWalletCompatibilityError,
} from '../../../errorHandling';
import { addBreadcrumb, captureAndReportError } from '../../../errorReporting';
import {
  createTurboSolanaWalletAdapter,
  getWalletPublicKeyBase58,
  type TurboSolanaSignerMode,
  type TurboSolanaWalletAdapter,
} from '../../../wallet';
import {
  createTurboSolanaSigner,
  verifyTurboSignedDataItem,
} from '../../../turboSolanaSigning';
import type { UploadOptions, UploadResult, WalletAdapter } from '../../types';
import { formatWincToAr } from '../shared/format';
import {
  createReadableStreamFromBuffer,
  dataItemStreamToArrayBuffer,
} from '../shared/streams';
import type { ArDriveSignedDataItem, ArDriveTag } from '../shared/types';

const turboSignerModeCache = new Map<string, TurboSolanaSignerMode>();

type AuthenticatedTurboConfig = Parameters<
  typeof TurboFactory.authenticated
>[0];
type AuthenticatedTurboSigner = NonNullable<AuthenticatedTurboConfig['signer']>;
type RetryWithExponentialBackoff = <T>(
  operation: () => Promise<T>,
  options?: {
    maxRetries?: number;
    baseDelay?: number;
    maxDelay?: number;
    operationName?: string;
    onRetry?: (attempt: number, error: Error) => void;
  },
) => Promise<T>;
type ReportProgress = (
  uploadedBytes: number,
  totalBytes: number,
  onProgress?: UploadOptions['onProgress'],
  additionalInfo?: {
    message?: string;
    isSignatureInProgress?: boolean;
  },
) => void;

export class ArDriveTurboUploader {
  private turbo: TurboAuthenticatedClient | null = null;
  private walletAdapter: WalletAdapter | null = null;
  private turboWalletAdapter: TurboSolanaWalletAdapter | null = null;
  private isSignatureInProgress = false;
  private signerMode: TurboSolanaSignerMode = 'hex';

  constructor(
    private readonly options: {
      getErrorMessage: (error: unknown) => string;
      getTokenTools: (walletAdapter: WalletAdapter) => TokenTools | undefined;
      reportProgress: ReportProgress;
      retryWithExponentialBackoff: RetryWithExponentialBackoff;
    },
  ) {}

  async initialize(walletAdapter: WalletAdapter): Promise<void> {
    this.walletAdapter = walletAdapter;

    const wrappedAdapter = createTurboSolanaWalletAdapter(walletAdapter);
    this.turboWalletAdapter = wrappedAdapter;

    const signMessage = wrappedAdapter.signMessage;
    wrappedAdapter.signMessage = async (message: Uint8Array) => {
      this.isSignatureInProgress = true;

      try {
        return await signMessage(message);
      } finally {
        this.isSignatureInProgress = false;
      }
    };

    const walletAddress = getWalletPublicKeyBase58(walletAdapter);
    this.signerMode = turboSignerModeCache.get(walletAddress) ?? 'hex';
    this.turbo = this.createAuthenticatedTurboClient(this.signerMode);
  }

  isInitialized(): boolean {
    return this.turbo !== null;
  }

  getWalletAdapter(): WalletAdapter | null {
    return this.walletAdapter;
  }

  async signDataItem(
    buffer: Buffer,
    tags: ArDriveTag[],
    options?: UploadOptions,
  ): Promise<ArDriveSignedDataItem> {
    this.getTurboOrThrow();

    if (options?.onProgress) {
      this.options.reportProgress(0, buffer.length, options.onProgress, {
        isSignatureInProgress: true,
        message: 'Waiting for wallet signature...',
      });
    }

    try {
      return await this.signDataItemWithCompatibleSigner(buffer, tags, options);
    } finally {
      this.isSignatureInProgress = false;
    }
  }

  async uploadBuffer(
    buffer: Buffer,
    tags: ArDriveTag[],
    options?: UploadOptions,
  ): Promise<UploadResult> {
    this.getTurboOrThrow();

    const walletAddress = this.walletAdapter?.publicKey
      ? getWalletPublicKeyBase58(this.walletAdapter)
      : 'unknown';

    console.log(`[ArDrive] Starting upload with wallet: ${walletAddress}`);
    console.log(`[ArDrive] Buffer size: ${buffer.length} bytes`);

    return this.options.retryWithExponentialBackoff(
      async () => {
        const uploadOptions = this.createUploadOptions(
          () => createReadableStreamFromBuffer(buffer),
          () => buffer.length,
          tags,
          options,
        );

        try {
          const uploadResult =
            await this.getTurboOrThrow().uploadFile(uploadOptions);
          this.reportUploadComplete(buffer.length, options);
          return this.toUploadResult(uploadResult.id, buffer.length);
        } catch (error) {
          if (isWalletCompatibilityError(error)) {
            throw new Error(ARDRIVE_WALLET_COMPATIBILITY_ERROR);
          }

          const message = this.options.getErrorMessage(error).toLowerCase();
          if (!message.includes('invalid data item') || !this.walletAdapter) {
            throw error;
          }

          const fallbackMode: TurboSolanaSignerMode =
            this.signerMode === 'hex' ? 'raw' : 'hex';

          if (options?.onProgress) {
            this.options.reportProgress(0, buffer.length, options.onProgress, {
              isSignatureInProgress: true,
              message: 'Retrying wallet signature compatibility...',
            });
          }

          const fallbackTurbo =
            this.createAuthenticatedTurboClient(fallbackMode);
          const fallbackWalletAddress = getWalletPublicKeyBase58(
            this.walletAdapter,
          );
          this.signerMode = fallbackMode;
          turboSignerModeCache.set(fallbackWalletAddress, fallbackMode);
          this.turbo = fallbackTurbo;

          try {
            const uploadResult = await fallbackTurbo.uploadFile(uploadOptions);
            this.reportUploadComplete(buffer.length, options);
            return this.toUploadResult(uploadResult.id, buffer.length);
          } catch (fallbackError) {
            if (isWalletCompatibilityError(fallbackError)) {
              throw new Error(ARDRIVE_WALLET_COMPATIBILITY_ERROR);
            }

            if (
              this.options
                .getErrorMessage(fallbackError)
                .toLowerCase()
                .includes('invalid data item')
            ) {
              throw new Error(ARDRIVE_WALLET_COMPATIBILITY_ERROR);
            }

            throw fallbackError;
          }
        }
      },
      {
        operationName: 'upload_with_turbo_buffer',
        maxRetries: options?.maxRetries || 3,
        onRetry: (attempt, error) => {
          console.log(
            `ArDrive upload attempt ${attempt} failed: ${error.message}`,
          );
          console.log(`[ArDrive] Wallet used for upload: ${walletAddress}`);
          captureAndReportError(error, {
            operation: 'ArDrive.uploadFromBuffer',
            component: 'ArDriveProvider',
            attempt,
            walletAddress,
            bufferSize: buffer.length,
            contentType: options?.contentType,
            tagCount: tags.length,
            tags: tags.map((tag) => ({
              name: tag.name,
              valueLen: tag.value.length,
            })),
          });
        },
      },
    );
  }

  async getBalance(): Promise<{ balance: string; currency: string }> {
    const turbo = this.getTurboOrThrow();

    try {
      const { winc } = await turbo.getBalance();
      return {
        balance: formatWincToAr(winc),
        currency: 'AR',
      };
    } catch (error) {
      throw new Error(
        `Failed to get balance: ${this.options.getErrorMessage(error)}`,
      );
    }
  }

  async getUploadCost(
    bytes: number,
  ): Promise<{ cost: string; currency: string }> {
    if (!this.turbo) {
      const unauthenticatedTurbo = TurboFactory.unauthenticated();
      const [uploadCost] = await unauthenticatedTurbo.getUploadCosts({
        bytes: [bytes],
      });

      return {
        cost: formatWincToAr(uploadCost.winc),
        currency: 'AR',
      };
    }

    try {
      const [uploadCost] = await this.turbo.getUploadCosts({ bytes: [bytes] });
      return {
        cost: formatWincToAr(uploadCost.winc),
        currency: 'AR',
      };
    } catch (error) {
      throw new Error(
        `Failed to get upload cost: ${this.options.getErrorMessage(error)}`,
      );
    }
  }

  private createAuthenticatedTurboClient(
    signerMode: TurboSolanaSignerMode,
  ): TurboAuthenticatedClient {
    if (!this.turboWalletAdapter) {
      throw new Error('ArDrive provider not initialized');
    }

    const signer = createTurboSolanaSigner(
      this.turboWalletAdapter,
      signerMode,
    ) as unknown as AuthenticatedTurboSigner;

    return TurboFactory.authenticated({
      signer,
      token: 'solana',
      tokenTools: this.walletAdapter
        ? this.options.getTokenTools(this.walletAdapter)
        : undefined,
    });
  }

  private async signDataItemWithCompatibleSigner(
    buffer: Buffer,
    tags: ArDriveTag[],
    options?: UploadOptions,
  ): Promise<ArDriveSignedDataItem> {
    const currentTurbo = this.getTurboOrThrow();

    if (!this.walletAdapter) {
      throw new Error('ArDrive provider not initialized');
    }

    const walletAddress = getWalletPublicKeyBase58(this.walletAdapter);
    const fallbackMode: TurboSolanaSignerMode =
      this.signerMode === 'hex' ? 'raw' : 'hex';
    const attemptedModes: TurboSolanaSignerMode[] = [];
    let lastError: unknown;
    let sawInvalidSignature = false;

    for (const signerMode of [this.signerMode, fallbackMode]) {
      if (attemptedModes.includes(signerMode)) {
        continue;
      }

      attemptedModes.push(signerMode);

      const turboClient =
        signerMode === this.signerMode
          ? currentTurbo
          : this.createAuthenticatedTurboClient(signerMode);

      if (signerMode !== this.signerMode && options?.onProgress) {
        this.options.reportProgress(0, buffer.length, options.onProgress, {
          isSignatureInProgress: true,
          message: 'Retrying wallet signature compatibility...',
        });
      }

      try {
        const signed = await turboClient.signer.signDataItem({
          fileStreamFactory: () => createReadableStreamFromBuffer(buffer),
          fileSizeFactory: () => buffer.length,
          dataItemOpts: {
            tags,
          },
        });

        const dataItemArrayBuffer = await dataItemStreamToArrayBuffer(
          signed.dataItemStreamFactory(),
        );
        const signedBytes = new Uint8Array(dataItemArrayBuffer);
        const isValidDataItem = await verifyTurboSignedDataItem(signedBytes);

        if (!isValidDataItem) {
          sawInvalidSignature = true;
          addBreadcrumb(
            'ArDrive signed data item failed local verification',
            'arweave',
            {
              walletAddress,
              signerMode,
              attemptedModes: attemptedModes.join(','),
              bufferSize: buffer.length,
              tagCount: tags.length,
            },
          );
          continue;
        }

        this.signerMode = signerMode;
        turboSignerModeCache.set(walletAddress, signerMode);
        this.turbo = turboClient;

        if (options?.onProgress) {
          this.options.reportProgress(0, buffer.length, options.onProgress, {
            message: 'Signature complete, uploading...',
          });
        }

        const reportedSize = signed.dataItemSizeFactory();
        const dataItemSize =
          reportedSize === signedBytes.length
            ? reportedSize
            : signedBytes.length;

        return {
          signedBytes,
          signedBytesBuffer: dataItemArrayBuffer,
          dataItemSize,
        };
      } catch (error) {
        if (isUserRejectedError(error)) {
          throw error;
        }

        if (isWalletCompatibilityError(error)) {
          throw new Error(ARDRIVE_WALLET_COMPATIBILITY_ERROR);
        }

        lastError = error;
      }
    }

    if (sawInvalidSignature && !lastError) {
      captureAndReportError('ArDrive signed data item failed verification', {
        operation: 'ArDrive.signDataItemWithCompatibleSigner',
        component: 'ArDriveProvider',
        walletAddress,
        signerMode: this.signerMode,
        attemptedModes,
      });
      throw new Error(ARDRIVE_WALLET_COMPATIBILITY_ERROR);
    }

    throw lastError instanceof Error
      ? lastError
      : new Error('Failed to sign data item');
  }

  private createUploadOptions(
    fileStreamFactory: () => ReadableStream,
    fileSizeFactory: () => number,
    tags: ArDriveTag[],
    options?: UploadOptions,
  ): {
    fileStreamFactory: () => ReadableStream;
    fileSizeFactory: () => number;
    signal: AbortSignal;
    dataItemOpts: {
      tags: ArDriveTag[];
    };
    onProgress?: (progress: {
      uploadedBytes: number;
      totalBytes: number;
    }) => void;
  } {
    const uploadOptions = {
      fileStreamFactory,
      fileSizeFactory,
      signal: new AbortController().signal,
      dataItemOpts: {
        tags,
      },
    };

    if (!options?.onProgress) {
      return uploadOptions;
    }

    let lastReportedPercent = 0;
    const progressCallback = options.onProgress;

    return {
      ...uploadOptions,
      onProgress: (progress) => {
        const percent = Math.round(
          (progress.uploadedBytes / progress.totalBytes) * 100,
        );

        if (percent !== lastReportedPercent || this.isSignatureInProgress) {
          lastReportedPercent = percent;
          this.options.reportProgress(
            progress.uploadedBytes,
            progress.totalBytes,
            progressCallback,
            { isSignatureInProgress: this.isSignatureInProgress },
          );
        }
      },
    };
  }

  private getTurboOrThrow(): TurboAuthenticatedClient {
    if (!this.turbo) {
      throw new Error('ArDrive provider not initialized');
    }

    return this.turbo;
  }

  private reportUploadComplete(
    totalBytes: number,
    options?: UploadOptions,
  ): void {
    if (!options?.onProgress) {
      return;
    }

    this.options.reportProgress(totalBytes, totalBytes, options.onProgress, {
      message: 'Upload complete',
    });
  }

  private toUploadResult(id: string, size: number): UploadResult {
    return {
      id,
      url: buildTurboGatewayUrl(id),
      timestamp: Date.now(),
      size,
    };
  }
}
